import React, { useState } from 'react';
import {
    Button,
    ConfirmationDialogBox,
    CopyIcon,
    Divider,
    EditIcon,
    TrashIcon,
} from '@placer-ui/components';
import { Button as LegacyButton } from 'ui-components/button/button';
import { EditIconCopy, GoToIcon } from 'components/assets/Icons/Icons';
import { fetchWrapper } from 'API/authentification/fetch-wrapper';
import { useOpenSupportRequest } from 'features/offline-reports/support-requests/components/support-requests-modals/support-modal-manager';
import { openChainsReport } from 'utils/navigation/navigation';
import { usePOI } from 'features/explore/model/map/use-poi';
import { useSendExploreSplunkEvent } from 'features/explore/hooks/use-explore-tracking';
import {
    useDeleteResult,
    useSetLoading,
    useSetTriggerCreatePOI,
} from 'features/explore/context/use-explore-ui-state-actions';
import { COORDINATES_ENTITY } from 'features/explore/constants';
import { useExploreUIState } from 'features/explore/context/use-explore-ui-state';
import style from './explore-drawer-footer.module.scss';
import { useSelector } from 'react-redux';
import { userSelector } from 'store/selectors/app-selectors';
import { BasePlaceEntity } from '@placer-ui/types';
import { useFullscreenContext } from 'utils/fullscreen-provider/fullscreen-provider';
import { useExploreFacade } from 'features/explore/model/hooks/use-explore-facade';

type ExploreDrawerFooterProps = {
    poiInfo: BasePlaceEntity;
};

export const ExploreDrawerFooter = ({ poiInfo }: ExploreDrawerFooterProps) => {
    const { openSupportRequest } = useOpenSupportRequest();
    const sendSplunkEvent = useSendExploreSplunkEvent();
    const setTriggerCreatePOI = useSetTriggerCreatePOI();
    const { customPoiAddresses } = useExploreUIState();
    const user = useSelector(userSelector);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const { isFullscreen, toggleFullscreen } = useFullscreenContext();
    const exploreFacade = useExploreFacade();
    const setIsLoading = useSetLoading();
    const deleteResult = useDeleteResult();

    const { id, type, name, category, parent_chain_id, provider_data, is_custom_poi } = poiInfo;
    const providerDataEntityId = provider_data?.entity_id;
    const poi = usePOI(id, type);
    const isCoordinate =
        (category === 'Address' && !customPoiAddresses[providerDataEntityId || id]) ||
        providerDataEntityId === COORDINATES_ENTITY;
    const showCustomPOIFooter = is_custom_poi && !!poi;
    let isCustomPOIOwner = false;
    if (showCustomPOIFooter) {
        isCustomPOIOwner = poi.creator.get()?.user_email === user?.email;
    }

    const onOpenChainReport = () => {
        sendSplunkEvent({
            component: 'Report Options Panel',
            action: 'open report',
            value: 'chain',
        });
        openChainsReport(parent_chain_id);
    };

    const updateCPOI = () => {
        setTriggerCreatePOI({ poiId: poiInfo.id, mode: 'update' });
    };

    const copyCPOI = () => {
        setTriggerCreatePOI({ poiId: poiInfo.id, mode: 'copy' });
    };

    const openDeleteModal = () => {
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
    };

    const deleteCPOI = async () => {
        try {
            closeDeleteModal();
            setIsLoading(true);
            await fetchWrapper({
                targetUrl: `/custom-poi/v2/complexes/${poiInfo.id}/delete`,
                method: 'DELETE',
            });
            exploreFacade.clearDrawerPoi();
            deleteResult(poiInfo.id);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {!is_custom_poi && (
                <div className={style.footerOptions}>
                    {parent_chain_id && (
                        <div className={style.option}>
                            <LegacyButton
                                data-testid="open-report"
                                onClick={onOpenChainReport}
                                className={style.suggestEditBtn}
                                icon={<GoToIcon />}
                            >
                                <span className={style.menuText}>
                                    Open a Chain Report for {name}
                                </span>
                            </LegacyButton>
                        </div>
                    )}
                    <div className={style.option}>
                        <LegacyButton
                            data-testid={isCoordinate ? 'request-traffic-pin' : 'suggest-edit'}
                            onClick={() => {
                                isFullscreen && toggleFullscreen();
                                if (isCoordinate) {
                                    openSupportRequest({
                                        flowType: 'trafficPin',
                                        props: {
                                            address:
                                                poiInfo.address.formatted_address ??
                                                poiInfo.address.address,
                                            lat: poiInfo.geolocation?.lat,
                                            lng: poiInfo.geolocation?.lng,
                                        },
                                    });
                                } else {
                                    openSupportRequest({
                                        flowType: 'suggestEdit',
                                        props: {
                                            address:
                                                poiInfo.address.formatted_address ??
                                                poiInfo.address.address,
                                            poiName: poiInfo.name as string,
                                            id: poiInfo.id,
                                            entityType: poiInfo.type,
                                        },
                                    });
                                }
                            }}
                            className={style.suggestEditBtn}
                            icon={<EditIconCopy />}
                        >
                            <span className={style.menuText}>
                                {isCoordinate ? 'Request a Traffic Pin' : 'Suggest an edit'}
                            </span>
                        </LegacyButton>
                    </div>
                </div>
            )}
            {showCustomPOIFooter && (
                <div className={style.cpoiFooterOptions}>
                    {isCustomPOIOwner && (
                        <>
                            <Button
                                size={'small'}
                                testId={'edit-cpoi'}
                                LeftIcon={<EditIcon />}
                                variant={'text-gray'}
                                onClick={updateCPOI}
                            >
                                Edit
                            </Button>
                            <Divider isVertical={true} margin={'0 16px'} />
                        </>
                    )}
                    <Button
                        size={'small'}
                        testId={'clone-cpoi'}
                        LeftIcon={<CopyIcon />}
                        variant={'text-gray'}
                        onClick={copyCPOI}
                    >
                        Clone
                    </Button>
                    {isCustomPOIOwner && (
                        <>
                            <Divider isVertical={true} margin={'0 16px'} />
                            <Button
                                size={'small'}
                                testId={'delete-cpoi'}
                                LeftIcon={<TrashIcon color={'gray'} />}
                                variant={'text-gray'}
                                onClick={openDeleteModal}
                            >
                                Delete
                            </Button>
                        </>
                    )}
                </div>
            )}
            <ConfirmationDialogBox
                open={isDeleteModalOpen}
                onOpenChange={setIsDeleteModalOpen}
                title="Are You Sure?"
                content={`You are about to delete "${name}". This action cannot be undone.`}
                illustration="DeleteIllustration"
                actionsOrientation="horizontal"
                hasBackdrop
                actions={
                    <>
                        <Button
                            testId="delete-cpoi-cancel-button"
                            variant="secondary-blue"
                            onClick={closeDeleteModal}
                            fullWidth
                        >
                            Cancel
                        </Button>
                        <Button
                            testId="delete-cpoi-confirm-button"
                            variant="primary-brand"
                            onClick={deleteCPOI}
                            fullWidth
                        >
                            Delete
                        </Button>
                    </>
                }
            />
        </>
    );
};
